import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DropdownAtom } from "../../../components/atoms/atom";

/**
 * Cost Group Dropdown component with built-in hierarchical formatting
 *
 * @param {Object} props - Component props
 * @param {Array} props.costGroups - List of DIN276 cost groups from backend (with id, code, name, parentId, level)
 * @param {string} props.value - Selected cost group ID
 * @param {Function} props.onChange - Handler for dropdown change
 * @param {boolean} props.isClearable - Whether dropdown allows clearing selection
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.size - Dropdown size ('small', 'medium', 'large')
 * @param {Object} props.icon - Icon element to display in dropdown
 * @param {boolean} props.isTableCell - Whether the dropdown is used inside a table cell
 * @param {string} props.placeholder - Custom placeholder text
 * @param {boolean} props.hideIgnoreCategory - Whether to hide the "Ignorieren" (000) category
 */
const CostGroupDropdown = ({
    costGroups = [],
    value = null,
    onChange,
    isClearable = true,
    className = "",
    size = "small",
    icon = null,
    isTableCell = false,
    disabled = false,
    placeholder = null,
    label = "",
    hideIgnoreCategory = false,
    required = false,
    error = "",
}) => {
    const { t } = useTranslation();

    // Format cost groups into hierarchical structure based on backend data
    const formattedCostGroups = useMemo(() => {
        if (!costGroups?.length) {
            return [];
        }

        // Check if costGroups is already formatted (has 'options' property)
        if (costGroups[0]?.options) {
            // Optionally filter out "Ignorieren" group if already grouped
            if (hideIgnoreCategory) {
                return costGroups.filter(
                    (group) =>
                        !group.label.startsWith("000") &&
                        !group.label.startsWith("0 -"),
                );
            }
            return costGroups; // Already formatted, return as-is
        }

        // Build a map of all items by ID for quick lookup
        const itemsById = {};
        costGroups.forEach((group) => {
            itemsById[group.id] = group;
        });

        // Helper function to find the root parent (top-level category)
        const findRootParent = (item) => {
            let current = item;
            while (current.parentId !== null) {
                current = itemsById[current.parentId];
                if (!current) break; // Safety check
            }
            return current;
        };

        // Build parent categories and collect all descendants
        const parentCategories = {};
        const result = [];

        costGroups.forEach((group) => {
            if (group.parentId === null) {
                // This is a main category (level 1)
                if (group.code === "000") {
                    // Special handling for "000 - Ignorieren" - add as standalone selectable option
                    if (!hideIgnoreCategory) {
                        result.push({
                            label: `${group.code} - ${group.name}`,
                            options: [{
                                value: group.id,
                                label: `${group.code} - ${group.name}`,
                            }],
                        });
                    }
                } else {
                    // Main categories like 100, 200, etc. become group headers
                    // Also add themselves as the first selectable option within the group
                    parentCategories[group.id] = {
                        label: `${group.code} - ${group.name}`,
                        options: [
                            {
                                value: group.id,
                                label: `${group.code} - ${group.name}`,
                            },
                        ],
                    };
                }
            } else {
                // This is a child/descendant item - find its root parent and add it there
                const rootParent = findRootParent(group);
                if (rootParent && parentCategories[rootParent.id]) {
                    parentCategories[rootParent.id].options.push({
                        value: group.id,
                        label: `${group.code} - ${group.name}`,
                    });
                }
            }
        });

        // Add all parent groups (with children) to the result
        Object.values(parentCategories).forEach((group) => {
            if (group.options.length > 0) {
                result.push(group);
            }
        });

        return result;
    }, [costGroups, hideIgnoreCategory]);

    return (
        <div
            className={`cost-group-dropdown ${isTableCell ? "table-cell" : ""} ${className}`}
        >
            <DropdownAtom
                placeholder={
                    placeholder ||
                    t("lv.cost-group-assignment.select-cost-group")
                }
                label={label}
                options={formattedCostGroups}
                value={value}
                onChange={onChange}
                isClearable={isClearable}
                size={size}
                icon={icon}
                menuPlacement="auto"
                disabled={disabled}
                className="cost-group-select"
                isGrouped={true}
                isCollapsible={true}
                error={error}
                closeMenuOnScroll={(e) =>
                    e.target.matches("tbody.scrollable-tbody") ? true : false
                }
                required={required}
            />
        </div>
    );
};

export default CostGroupDropdown;
